import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { ContractTabs } from 'components/organisms/forms/contract/ContractForm';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useCreateContractForLabel } from 'services/contract';
import { CONTRACT_STATUS } from 'services/contract/types';
import { IContractTemplateForm } from 'services/templates/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { contractTemplateUtil } from 'utils';
import { formatDateToInput } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

export const contractInitialValues: IContractTemplateForm = {
  ...contractTemplateUtil.form.initialValues,
  items: contractTemplateUtil.contract.initialValues,
  details: {
    withEndDate: false,
    status: CONTRACT_STATUS.offer,
    endDate: formatDateToInput(new Date()),
    startDate: formatDateToInput(new Date()),
  },
};

const CreateContract = () => {
  const labelId = useParamId('/label');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const goBackToLabel = () => navigate(`/label/${labelId}/contract`);

  const createContract = useCreateContractForLabel();
  const onCreateContract = async (values: IContractTemplateForm) => {
    const contract = middleware.contract.formToQuery(values);

    if (
      !contract.digitalDistributionSection &&
      !contract.merchDistributionSection &&
      !contract.channelDistributionSection &&
      !contract.physicalDistributionSection
    ) {
      return toast.error(t('formik-error.at-least-one-distribution'));
    }

    await createContract.mutateAsync(
      { ...contract, labelId },
      {
        onSuccess: contract =>
          navigate(`/label/${labelId}/contract/${contract.id}/form`),
        onError: error => onErrorShowAllErrorsToast(error),
      },
    );
  };

  return (
    <Form.Contract
      customBackItem={
        <Button type='button' onlyIcon onClick={goBackToLabel}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <>
          <Button type='submit' variant={BUTTON_VARIANT.submit}>
            <Button.Text text={t(`button.create`)} />
          </Button>
        </>
      }
      withTabs
      disabledTabs={[
        ContractTabs.products,
        ContractTabs.tracks,
        ContractTabs.videos,
        ContractTabs.channelVideos,
      ]}
      initialStatus={{
        ...contractTemplateUtil.contract.initialStatus
      }}
      initialValues={contractInitialValues}
      onSubmit={onCreateContract}
    />
  );
};

export default CreateContract;
